import {
  DateTimePicker,
  FormInput,
  FormSelect,
  PrimaryButton,
  setDateWithTimezone,
  toast,
} from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import { InternalError } from '@systemeio/util'
import { CouponErrorsInterface } from 'modules/coupons/types/coupon-interface'
import { getDiscountTypeData } from 'modules/funnels/funnel/components/step-configuration-tab/coupon-selector/utils/coupons-utils'
import React, { useState } from 'react'
import ButtonLikeLink from 'shared/components/button-like-link'
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { filterPassedTime } from 'shared/utils/date-time-format'
import { CouponCreateInterface } from '../types/add-coupon-data-interface'
import { CloseIcon, SaveIcon } from '@systemeio/ui/icons'

interface CreateCouponFormProps {
  onCreateCoupon: (value: CouponCreateInterface) => Promise<void>
  openAddForm: () => void
  closeForm: () => void
  createFieldErrors: CouponErrorsInterface
}

const defaultValues: CouponCreateInterface = {
  innerName: '',
  code: '',
  discountType: undefined,
  discountAmount: undefined,
  currency: undefined as unknown as CouponCreateInterface['currency'],
  expirationDate: null,
  limitOfUse: NaN,
}

function CreateCouponForm({
  onCreateCoupon,
  openAddForm,
  closeForm,
  createFieldErrors,
}: CreateCouponFormProps) {
  const { user, intlLocale } = useUser()
  const { t } = useLocoTranslation()
  const [tempState, setTempState] = useState(defaultValues)
  const [isFetching, setIsFetching] = useState(false)
  const [error, setError] = useState('')

  const handleConfirm = async () => {
    try {
      setError('')
      setIsFetching(true)
      await onCreateCoupon(tempState)
      setIsFetching(false)
      closeForm()
    } catch (e) {
      setIsFetching(false)
      if (e instanceof BadRequest) {
        if (e.errors.common) {
          setError(e.errors.common.join(''))
        }
      } else if (e instanceof InternalError) {
        setError(t('core.error.internal_error_message'))
      } else {
        toast.error(t('global.error'))
      }
    }
  }

  return (
    <div className="border-text-secondary/30 relative flex flex-col gap-5 rounded-lg border px-5 py-3">
      <CloseIcon
        className={`cursor-pointer ${
          isFetching && 'fill-text-secondary pointer-events-none'
        } absolute right-1 top-1 lg:right-2 lg:top-2`}
        onClick={() => closeForm()}
      />
      <FormInput
        required
        label={t('dashboard.coupon.coupon_name')}
        labelClassName={'lg:text-[15px] text-sm text-text-primary font-medium'}
        placeholder={t('global.name')}
        value={tempState.innerName}
        onChange={e =>
          setTempState(prev => {
            return { ...prev, innerName: e.target.value }
          })
        }
        maxLength={256}
        error={createFieldErrors.innerName}
      />
      <div className="flex gap-5">
        <FormInput
          required
          label={t('dashboard.coupon.coupon_code')}
          labelClassName={'lg:text-[15px] text-sm text-text-primary font-medium'}
          placeholder={t('dashboard.coupon.coupon_code')}
          value={tempState.code}
          onChange={e =>
            setTempState(prev => {
              return { ...prev, code: e.target.value }
            })
          }
          maxLength={256}
          error={createFieldErrors.code}
        />
        <FormSelect
          label={t('dashboard.funnel.configuration.offer.coupon_discount_type')}
          labelClassName={'lg:text-[15px] text-sm text-text-primary font-medium'}
          data={getDiscountTypeData(t)}
          onChange={data =>
            setTempState(prev => {
              return { ...prev, discountType: data }
            })
          }
          value={tempState.discountType}
          required
          error={createFieldErrors.discountType}
        />
      </div>
      <FormInput
        required
        label={t('dashboard.coupon.discount_amount')}
        labelClassName={'lg:text-[15px] text-sm text-text-primary font-medium'}
        type="number"
        onChange={e =>
          setTempState(prev => {
            return { ...prev, discountAmount: e.target.valueAsNumber }
          })
        }
        value={
          !tempState.discountAmount && tempState.discountAmount !== 0
            ? ''
            : tempState.discountAmount
        }
        min={0}
        maxLength={256}
        error={createFieldErrors.discountAmount}
      />
      <div className="flex gap-5">
        <DateTimePicker
          value={tempState.expirationDate}
          onChange={data =>
            setTempState(prev => {
              return { ...prev, expirationDate: data }
            })
          }
          label={t('dashboard.coupon.expiration_date')}
          customProps={{
            minDate: setDateWithTimezone('', user?.timezone),
            filterTime: time => filterPassedTime(time, user?.timezone),
          }}
          key="coupon-expirationDate-create"
          labelClassName={'lg:text-[15px] text-sm text-text-primary font-medium'}
          error={createFieldErrors.expirationDate}
          createdAt={user?.createdAt}
          timezone={user?.timezone}
          userDateFormat={user?.dateFormat}
          userTimeFormat={user?.timeFormat}
          intlLocale={intlLocale}
        />
        <FormInput
          label={t('dashboard.coupon.limit_of_use')}
          type="number"
          onChange={e =>
            setTempState(prev => {
              return { ...prev, limitOfUse: e.target.valueAsNumber }
            })
          }
          value={!tempState.limitOfUse && tempState.limitOfUse !== 0 ? '' : tempState.limitOfUse}
          labelClassName={'lg:text-[15px] text-sm text-text-primary font-medium'}
          min={0}
          maxLength={256}
          error={createFieldErrors.limitOfUse}
        />
      </div>
      <div
        className={
          'text-text-primary flex w-full flex-col gap-2 text-sm font-medium lg:flex-row lg:text-[15px]'
        }
      >
        <span>{t('dashboard.funnel.configuration.offer.create_coupon_footer')}</span>
        <ButtonLikeLink
          onClick={() => {
            openAddForm()
          }}
        >
          {t('dashboard.funnel.configuration.offer.create_coupon_footer_link')}
        </ButtonLikeLink>
      </div>
      <div className="flex">
        <PrimaryButton
          width="large"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault()
            handleConfirm()
          }}
          isFetching={isFetching}
          type="submit"
        >
          <SaveIcon />
          {t('global.save')}
        </PrimaryButton>
      </div>
      <FieldErrorAndDescription error={error} errorClassName={'text-center mt-2'} />
    </div>
  )
}

export default CreateCouponForm
